import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Tag, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { Dropdown } from 'primereact/dropdown';
import api from '@/lib/api';
import { getApiErrorMessage } from '@/lib/toast';
import { Combobox } from '@/components/ui/combobox';
import { useAuthStore } from '@/stores/auth.store';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Link } from 'react-router-dom';
import { TAX_RATES_PATH } from '@/lib/tax';

interface CategoryTaxRate {
  id?: string;
  name: string;
  rate?: number | string;
  type?: string;
  isActive?: boolean;
  fbrSroScheduleNo?: string | null;
  fbrScenarioId?: string | null;
}

interface CategoryTaxLink {
  taxRateId: string;
  taxRate?: CategoryTaxRate | null;
}

interface CategoryRow {
  id: string;
  name: string;
  slug?: string | null;
  description?: string | null;
  parentId?: string | null;
  sortOrder?: number | null;
  markupPercent?: number | string | null;
  fbrSroScheduleNo?: string | null;
  fbrScenarioId?: string | null;
  skipPaymentMethodTax?: boolean | null;
  taxes?: CategoryTaxLink[];
  _count?: { products?: number };
}

interface TaxRateRow extends CategoryTaxRate {
  id: string;
}

interface FbrSroSchedule {
  sroDesc: string;
}

interface CategoryPayload {
  name: string;
  slug?: string;
  description?: string;
  parentId?: string;
  sortOrder: number;
  markupPercent?: number;
  taxRateIds: string[];
  fbrSroScheduleNo?: string;
  fbrScenarioId?: string;
  skipPaymentMethodTax: boolean;
}

interface PagedList<T> {
  data?: T[];
  meta?: { total?: number };
  total?: number;
}

function getEffectiveCategoryFbr(category: CategoryRow) {
  if (category.fbrSroScheduleNo || category.fbrScenarioId) {
    return { scheduleNo: category.fbrSroScheduleNo, scenarioId: category.fbrScenarioId, source: 'override' as const };
  }
  for (const t of (category.taxes ?? [])) {
    if (t.taxRate?.fbrSroScheduleNo || t.taxRate?.fbrScenarioId) {
      return { scheduleNo: t.taxRate.fbrSroScheduleNo, scenarioId: t.taxRate.fbrScenarioId, source: 'tax-rate' as const, taxRateName: t.taxRate.name };
    }
  }
  return null;
}

const defaultForm = {
  name: '',
  slug: '',
  description: '',
  parentId: '',
  sortOrder: '0',
  markupPercent: '',
  taxRateIds: [] as string[],
  fbrSroScheduleNo: '',
  fbrScenarioId: '',
  skipPaymentMethodTax: false,
};

export function CategoriesPage() {
  const { t } = useTranslation('products');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('products:create') || hasPermission('products:*') || hasPermission('inventory:create') || hasPermission('inventory:*');
  const canUpdate = hasPermission('products:update') || hasPermission('products:*') || hasPermission('inventory:update') || hasPermission('inventory:*');
  const canDelete = hasPermission('products:delete') || hasPermission('products:*') || hasPermission('inventory:delete') || hasPermission('inventory:*');
  const canViewFbr = hasPermission('fbr:*') || hasPermission('fbr:read');
  const canOpenTaxRates = hasPermission('tax-rates:read') || hasPermission('tax-rates:*');

  const fbrScenarioOptions = [
    { label: t('fbrScenarioNone'), value: '' },
    { label: t('fbrScenarioSN001'), value: 'SN001' },
    { label: t('fbrScenarioSN002'), value: 'SN002' },
    { label: t('fbrScenarioSN005'), value: 'SN005' },
    { label: t('fbrScenarioSN006'), value: 'SN006' },
    { label: t('fbrScenarioSN007'), value: 'SN007' },
    { label: t('fbrScenarioSN008'), value: 'SN008' },
    { label: t('fbrScenarioSN016'), value: 'SN016' },
    { label: t('fbrScenarioSN017'), value: 'SN017' },
    { label: t('fbrScenarioSN024'), value: 'SN024' },
    { label: t('fbrScenarioSN026'), value: 'SN026' },
    { label: t('fbrScenarioSN027'), value: 'SN027' },
    { label: t('fbrScenarioSN028'), value: 'SN028' },
  ];

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const limit = 25;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editCategory, setEditCategory] = useState<CategoryRow | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({ open: false, id: '', name: '' });
  const queryClient = useQueryClient();

  useEffect(() => {
    if (dialogOpen && editCategory) {
      setForm({
        name: editCategory.name ?? '',
        slug: editCategory.slug ?? '',
        description: editCategory.description ?? '',
        parentId: editCategory.parentId ?? '',
        sortOrder: editCategory.sortOrder != null ? String(editCategory.sortOrder) : '0',
        markupPercent: editCategory.markupPercent != null ? String(editCategory.markupPercent) : '',
        taxRateIds: editCategory.taxes?.map((tx) => tx.taxRateId) ?? [],
        fbrSroScheduleNo: editCategory.fbrSroScheduleNo ?? '',
        fbrScenarioId: editCategory.fbrScenarioId ?? '',
        skipPaymentMethodTax: editCategory.skipPaymentMethodTax ?? false,
      });
      setSlugManuallyEdited(true);
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
      setSlugManuallyEdited(false);
    }
  }, [dialogOpen, editCategory]);

  const { data: categories, isLoading } = useQuery<CategoryRow[] | PagedList<CategoryRow>>({
    queryKey: ['categories', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search) params.set('search', search);
      const res = await api.get(`/categories?${params}`);
      return res.data;
    },
  });

  const { data: taxRates } = useQuery<TaxRateRow[] | PagedList<TaxRateRow>>({
    queryKey: ['tax-rates'],
    queryFn: async () => { const res = await api.get('/tax-rates'); return res.data; },
  });

  const { data: fbrSroSchedules } = useQuery<FbrSroSchedule[]>({
    queryKey: ['fbr-reference-sro'],
    queryFn: async () => { const res = await api.get('/fbr/reference/sro-schedules'); return res.data; },
    staleTime: 1000 * 60 * 60,
    enabled: canViewFbr,
  });

  const sroOptions = [
    { label: t('sroNoneNotApplicable'), value: '' },
    ...(fbrSroSchedules ?? [])
      .map((s) => ({ label: s.sroDesc, value: s.sroDesc }))
      .filter((o, i, arr) => arr.findIndex((x) => x.value === o.value) === i)
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload: CategoryPayload = {
        name: form.name,
        slug: form.slug || undefined,
        description: form.description || undefined,
        parentId: form.parentId || undefined,
        sortOrder: form.sortOrder ? Number(form.sortOrder) : 0,
        markupPercent: form.markupPercent ? Number(form.markupPercent) : undefined,
        taxRateIds: form.taxRateIds,
        fbrSroScheduleNo: form.fbrSroScheduleNo || undefined,
        fbrScenarioId: form.fbrScenarioId || undefined,
        skipPaymentMethodTax: form.skipPaymentMethodTax,
      };
      if (editCategory) {
        const { data } = await api.patch(`/categories/${editCategory.id}`, payload);
        return data;
      }
      const { data } = await api.post('/categories', payload);
      return data;
    },
    onSuccess: () => {
      toast.success(editCategory ? t('categoryUpdated') : t('categoryCreated'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('saveFailed')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/categories/${id}`),
    onSuccess: () => {
      toast.success(t('categoryDeleted'));
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: () => toast.error(t('deleteFailed')),
  });

  const categoriesList: CategoryRow[] = Array.isArray(categories) ? categories : categories?.data ?? [];
  const catTotal: number = (Array.isArray(categories) ? undefined : categories?.meta?.total ?? categories?.total) ?? categoriesList.length;
  const taxRatesList: TaxRateRow[] = Array.isArray(taxRates) ? taxRates : taxRates?.data ?? [];

  function handleNameChange(name: string) {
    const updates: Partial<typeof form> = { name };
    if (!slugManuallyEdited) {
      updates.slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    }
    setForm({ ...form, ...updates });
  }

  function handleSlugChange(slug: string) {
    setSlugManuallyEdited(true);
    setForm({ ...form, slug });
  }

  function toggleTaxRate(taxRateId: string) {
    setForm((prev) => ({
      ...prev,
      taxRateIds: prev.taxRateIds.includes(taxRateId)
        ? prev.taxRateIds.filter((id) => id !== taxRateId)
        : [...prev.taxRateIds, taxRateId],
    }));
  }

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('categoriesTitle')}
        subtitle={t('categoriesSubtitle')}
        action={canCreate ? (
          <Button variant="gradient" onClick={() => { setEditCategory(null); setDialogOpen(true); }}>
            <Plus className="me-2 h-4 w-4" />
            {t('addCategory')}
          </Button>
        ) : undefined}
      />

      {isTestMode && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span><strong>{t('testModeLabel')}</strong> {t('viewingTestCategories')} <a href="/settings" className="underline">{t('settingsLink')}</a> {t('toManageLiveCategories')}</span>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <SearchInput
          placeholder={t('searchCategoriesPlaceholder')}
          value={search}
          onChange={(value) => { setSearch(value); setPage(1); }}
          className="max-w-sm"
        />
      </div>

      {/* Categories table */}
      <Card className="animate-fade-in-up">
        <CardContent className="p-0">
          <Table mobileCards>
            <THead>
              <Tr hover={false}>
                <Th>{t('categoryName')}</Th>
                <Th>{t('slug')}</Th>
                <Th className="text-end">{t('productsColumn')}</Th>
                <Th>{t('appliedTaxes')}</Th>
                <Th>{t('fbrSettings')}</Th>
                <Th className="text-end">{t('actionsColumn')}</Th>
              </Tr>
            </THead>
            <TBody>
              {isLoading ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="py-8">
                    <SkeletonTable rows={5} cols={6} />
                  </Td>
                </Tr>
              ) : categoriesList.length === 0 ? (
                <Tr hover={false} className="no-card">
                  <Td colSpan={6} className="p-0">
                    <EmptyState
                      icon={Tag}
                      title={t('noCategoriesFound')}
                      description={t('addCategoryAction')}
                      action={canCreate ? { label: t('addCategory'), onClick: () => { setEditCategory(null); setDialogOpen(true); } } : undefined}
                    />
                  </Td>
                </Tr>
              ) : (
                categoriesList.map((category) => (
                  <Tr key={category.id}>
                    <Td className="font-medium td-name">{category.name}</Td>
                    <Td label={t('slug')} className="text-muted-foreground td-hide">{category.slug || '—'}</Td>
                    <Td label={t('productsColumn')} className="text-end">{category._count?.products ?? 0}</Td>
                    <Td label={t('appliedTaxes')}>
                      {category.taxes && category.taxes.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {category.taxes.map((tx) => (
                            <span
                              key={tx.taxRateId}
                              className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/10 px-2 py-0.5 text-xs font-medium text-primary-text"
                            >
                              <Tag className="h-3 w-3" />
                              {tx.taxRate?.name?.includes('%') ? tx.taxRate.name : `${tx.taxRate?.name} ${Number(tx.taxRate?.rate)}%`}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </Td>
                    <Td label={t('fbrSettings')} className="text-xs td-hide">
                      {(() => {
                        const eff = getEffectiveCategoryFbr(category);
                        if (!eff) return <span className="text-muted-foreground">—</span>;
                        return (
                          <div className="space-y-1">
                            {eff.scheduleNo && (
                              <span className={`inline-flex items-center rounded px-1.5 py-0.5 font-mono text-xs border ${
                                eff.source === 'override'
                                  ? 'bg-warning/10 text-warning-text border-warning/30'
                                  : 'bg-muted text-muted-foreground border-border'
                              }`}>
                                {eff.scheduleNo}
                              </span>
                            )}
                            <div className="flex items-center gap-1">
                              {eff.scenarioId && (
                                <span className="text-muted-foreground">{eff.scenarioId}</span>
                              )}
                              {eff.source === 'override' && (
                                <span className="text-warning-text text-xs font-medium">⬆ {t('fbrOverrideLabel')}</span>
                              )}
                              {eff.source === 'tax-rate' && (
                                <span className="text-muted-foreground text-xs">{t('fbrFromTaxRate', { name: eff.taxRateName })}</span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </Td>
                    <Td className="text-end td-actions">
                      <TableRowActions
                        actions={[
                          {
                            key: 'edit',
                            label: t('editCategory'),
                            icon: Edit,
                            onSelect: () => { setEditCategory(category); setDialogOpen(true); },
                            permission: canUpdate,
                            primary: true,
                          },
                          {
                            key: 'delete',
                            label: t('deleteCategory'),
                            icon: Trash2,
                            onSelect: () => setDeleteConfirm({ open: true, id: category.id, name: category.name }),
                            permission: canDelete,
                            variant: 'destructive',
                          },
                        ]}
                      />
                    </Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {catTotal > limit && (
        <div className="border-t px-4 py-3 mt-1">
          <PaginationBar page={page} pageSize={limit} total={catTotal} onPageChange={setPage} />
        </div>
      )}

      {/* Add/Edit Category Dialog */}
      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editCategory ? t('editCategory') : t('addCategory')}
        width="52rem"
        submitLabel={editCategory ? t('updateCategory') : t('createCategory')}
        isPending={saveMutation.isPending}
        onSubmit={(e) => { e.preventDefault(); if (!form.name) return; saveMutation.mutate(); }}
        submitDisabled={!form.name}
      >
        <div className="space-y-4">
          <div>
            <Label>{t('categoryName')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder={t('categoryNamePlaceholder')}
              required
            />
          </div>

          <div>
            <Label>{t('slug')}</Label>
            <Input
              value={form.slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder={t('slugPlaceholder')}
            />
          </div>

          <div>
            <Label>{t('description')}</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder={t('optionalDescription')}
              rows={3}
            />
          </div>

          <div>
            <Label>{t('parentCategory')}</Label>
            <Combobox
              options={categoriesList
                .filter((cat) => !editCategory || cat.id !== editCategory.id)
                .map((cat) => ({ value: cat.id, label: cat.name }))}
              value={form.parentId}
              onChange={(val) => setForm({ ...form, parentId: val })}
              placeholder={t('parentNone')}
              searchPlaceholder={t('searchCategoriesPlaceholder')}
              emptyText={t('noCategoriesFound')}
              allowClear
            />
          </div>

          <div>
            <Label>{t('sortOrder')}</Label>
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
              placeholder="0"
              min={0}
              step={1}
            />
          </div>

          <div>
            <Label>Markup %</Label>
            <Input
              type="number"
              value={form.markupPercent}
              onChange={(e) => setForm({ ...form, markupPercent: e.target.value })}
              placeholder="Default markup for products without their own"
              min={0}
              step={0.01}
            />
          </div>

          {/* Tax Rates multi-select */}
          <div>
            <Label>{t('taxRatesLabel')}</Label>
            <div className="mt-2 space-y-2 rounded-md border border-input p-3 max-h-40 overflow-y-auto">
              {taxRatesList.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('noTaxRates')}
                  {canOpenTaxRates && (
                    <>
                      {' · '}
                      <Link to={TAX_RATES_PATH} className="font-medium text-primary-text underline">{t('openTaxRatesPage')}</Link>
                    </>
                  )}
                </p>
              ) : (
                taxRatesList
                  .filter((tr) => tr.isActive !== false)
                  .map((tr) => (
                    <Label key={tr.id} className="flex items-center gap-2 cursor-pointer font-normal leading-normal text-foreground">
                      <Checkbox
                        checked={form.taxRateIds.includes(tr.id)}
                        onChange={() => toggleTaxRate(tr.id)}
                      />
                      <span className="text-sm">
                        {tr.name.includes('%') ? tr.name : `${tr.name} ${Number(tr.rate)}%`} – {tr.type}
                      </span>
                    </Label>
                  ))
              )}
            </div>
          </div>

          {/* FBR overrides — only shown when FBR addon is active */}
          {canViewFbr && (
          <div className="border rounded-lg p-3 space-y-3 bg-warning/10 border-warning/30">
            <p className="text-xs font-semibold text-warning-text uppercase tracking-wide">{t('fbrDiOverride')}</p>
            <p className="text-xs text-muted-foreground">{t('fbrOverrideDescription')}</p>

            {/* Show inherited from selected tax rates */}
            {form.taxRateIds.length > 0 && (() => {
              const inherited = taxRatesList.filter((tr) => form.taxRateIds.includes(tr.id) && (tr.fbrSroScheduleNo || tr.fbrScenarioId));
              if (inherited.length === 0) return null;
              return (
                <div className="rounded-md bg-muted border border-border p-2 space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{t('inheritedFromTaxRates')}</p>
                  {inherited.map((tr) => (
                    <div key={tr.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-medium">{tr.name}:</span>
                      {tr.fbrSroScheduleNo && <span className="font-mono">{tr.fbrSroScheduleNo}</span>}
                      {tr.fbrScenarioId && <span className="text-muted-foreground">/ {tr.fbrScenarioId}</span>}
                    </div>
                  ))}
                  <p className="text-xs text-muted-foreground">{t('selectOverridesHint')}</p>
                </div>
              );
            })()}

            <div>
              <Label className="text-xs">{t('fbrSroScheduleNo')}</Label>
              <Dropdown
                value={form.fbrSroScheduleNo}
                onChange={(e) => setForm({ ...form, fbrSroScheduleNo: e.value || '' })}
                options={sroOptions}
                placeholder={t('noneInheritFromTaxRate')}
                className="w-full"
                showClear
              />
            </div>

            <div>
              <Label className="text-xs">{t('fbrScenarioOverride')}</Label>
              <Dropdown
                value={form.fbrScenarioId}
                onChange={(e) => setForm({ ...form, fbrScenarioId: e.value || '' })}
                options={fbrScenarioOptions}
                placeholder={t('noneInheritFromTaxRate')}
                className="w-full"
                showClear
              />
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="skipPaymentMethodTax"
                checked={form.skipPaymentMethodTax}
                onChange={(e) => setForm({ ...form, skipPaymentMethodTax: e.target.checked })}
                className="mt-0.5"
              />
              <div>
                <Label htmlFor="skipPaymentMethodTax" className="cursor-pointer text-xs font-medium">
                  {t('skipIctServiceTax')}
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {t('skipIctServiceTaxDesc')}
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </FormDialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteCategory')}
        message={t('confirmDeleteCategory', { name: deleteConfirm.name })}
        confirmLabel={t('deleteLabel')}
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSettled: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
